import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  postMethod,
  getMethod,
  patchMethod,
} from "../../services/ApiCallMethods";

// Add User
export const addUserManagement = createAsyncThunk(
  "userManagement/addUser",
  async (userPayload, { rejectWithValue }) => {
    try {
      const response = await postMethod(`/api/addUser`, userPayload);
      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Something went wrong");
    }
  }
);

// Get all users
export const getUserList = createAsyncThunk(
  "userManagement/getUserList",
  async (
    { currentPage, itemsPerPage, isActive, fromDate, toDate, search },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();

      if (isActive !== null) params.append("isActive", isActive);
      if (search) params.append("search", search);
      params.append("fromDate", fromDate);
      params.append("toDate", toDate);
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);
      const url = `/api/users?${params.toString()}`;

      const response = await getMethod(url);
      const json = await response.json();
      return json;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch users");
    }
  }
);

export const searchUser = createAsyncThunk(
  "userManagement/searchUser",
  async ({ currentPage, itemsPerPage, search }, { rejectWithValue }) => {
    try {
      const response = await getMethod(
        `/api/users?page=${currentPage}&limit=${itemsPerPage}&search=${search}`
      );
      const json = await response.json();
      return {
        data: json?.data || [],
        pagination: json?.pagination || {},
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data || "Failed to fetch users");
    }
  }
);

// Get user by ID
export const getUserWithId = createAsyncThunk(
  "userManagement/getUserWithId",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getMethod(`/api/users/${id}`);
      const json = await response.json();
      return json.data || json;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch user");
    }
  }
);

// Save (Update) user by ID
export const SaveUserWithId = createAsyncThunk(
  "userManagement/saveUserWithId",
  async ({ id, userPayload }, { rejectWithValue }) => {
    try {
      const response = await patchMethod(`/api/users/${id}`, userPayload);
      const json = await response.json();
      return json.data || json;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update user");
    }
  }
);

// Initial state
const initialState = {
  loading: false,
  error: null,
  success: false,
  data: null,
  users: [],
  usersLoading: false,
  usersError: null,
  userById: null,
  userByIdLoading: false,
  userByIdError: null,
  pagination: {},
  noFound: false,
};

// Slice
const userManagementSlice = createSlice({
  name: "userManagement",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Add User
    builder
      .addCase(addUserManagement.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addUserManagement.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.data = action.payload;
      })
      .addCase(addUserManagement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
        state.success = false;
      });

    // Get All Users
    builder
      .addCase(getUserList.pending, (state) => {
        state.usersLoading = true;
        state.usersError = null;
        state.users = []; // Clear old data
        state.pagination = {}; // Clear old pagination
      })
      .addCase(getUserList.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.users = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getUserList.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload;
      });

    // Get User by ID
    builder
      .addCase(getUserWithId.pending, (state) => {
        state.userByIdLoading = true;
        state.userByIdError = null;
        state.userById = null;
      })
      .addCase(getUserWithId.fulfilled, (state, action) => {
        state.userByIdLoading = false;
        state.userById = action.payload.user;
      })
      .addCase(getUserWithId.rejected, (state, action) => {
        state.userByIdLoading = false;
        state.userByIdError = action.payload;
      });
    // Save (Update) user by ID
    builder
      .addCase(SaveUserWithId.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(SaveUserWithId.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.data = action.payload;
      })
      .addCase(SaveUserWithId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });

    builder
      .addCase(searchUser.pending, (state) => {
        state.usersLoading = false;
        state.usersError = null;
        state.noFound = false;
        state.users = []; // Clear old data
        state.pagination = {}; // Clear old pagination
      })
      .addCase(searchUser.fulfilled, (state, action) => {
        state.usersLoading = false;

        if (
          Array.isArray(action.payload.data) &&
          action.payload.data.length > 0
        ) {
          state.users = action.payload.data;
          state.noFound = false;
        } else {
          // state.users = [];
          state.noFound = true;
        }

        state.pagination = action.payload.pagination;
      })
      .addCase(searchUser.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload;
        state.noFound = false;
      });
  },
});

export default userManagementSlice.reducer;
